// src/features/account/components/ProfileTab.jsx
import React, { useState, useEffect } from 'react';
import { User, Check, AlertCircle, Pencil } from 'lucide-react';
import { useWorkOS } from '../../workos/context/WorkOSContext';

const ProfileTab = () => {
  const { user, getValidAccessToken } = useWorkOS();

  const [displayName, setDisplayName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch current profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.accessToken) return;

      try {
        setIsLoading(true);
        const accessToken = await getValidAccessToken();

        const response = await fetch('/.netlify/functions/user-profile', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const data = await response.json();
        setDisplayName(data.display_name || '');
        setSavedName(data.display_name || '');

        // Auto-open edit mode if no display name set
        if (!data.display_name) {
          setIsEditing(true);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [user, getValidAccessToken]);

  const handleSave = async () => {
    const trimmed = displayName.trim();

    if (trimmed.length < 2) {
      setError('Display name must be at least 2 characters');
      return;
    }

    if (trimmed.length > 50) {
      setError('Display name must be 50 characters or less');
      return;
    }

    if (!/^[a-zA-Z0-9 _\-\.]+$/.test(trimmed)) {
      setError('Display name can only contain letters, numbers, spaces, hyphens, underscores, and periods');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      const accessToken = await getValidAccessToken();

      const response = await fetch('/.netlify/functions/user-profile', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ displayName: trimmed })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update display name');
      }

      const data = await response.json();
      setSavedName(data.display_name);
      setDisplayName(data.display_name);
      setIsEditing(false);
      setSuccessMessage('Display name updated across all your listings and comments');

      // Clear success message after a few seconds
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error('Error updating display name:', err);
      setError(err.message || 'Failed to update display name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(savedName);
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gray-900 rounded-lg p-6">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Display Name Section */}
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Display Name</h3>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          This is how your name appears across the Exchange — on your workflow listings and comments.
          Use this instead of your real name for privacy.
        </p>

        {!savedName && !isEditing && (
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4">
            <p className="text-blue-400 text-sm">
              You haven't set a display name yet. Set one to control how you appear on the Exchange.
            </p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
            <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
            <p className="text-green-400 text-sm">{successMessage}</p>
          </div>
        )}

        {isEditing ? (
          <div className="space-y-3">
            <input
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g., CoolCoder123, DevWizard"
              maxLength={50}
              autoFocus
              className="w-full max-w-md px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500">
              Letters, numbers, spaces, hyphens, underscores, and periods. 2–50 characters.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={isSaving || displayName.trim().length < 2}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              {savedName && (
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-white text-lg font-medium">{savedName}</span>
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors"
              title="Edit display name"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Account Info Section (read-only) */}
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-4">Account Info</h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm text-gray-400">Email</span>
            <p className="text-white">{user?.email || '—'}</p>
          </div>
          {(user?.firstName || user?.lastName) && (
            <div>
              <span className="text-sm text-gray-400">Name (from login provider)</span>
              <p className="text-white">{[user?.firstName, user?.lastName].filter(Boolean).join(' ')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
